
import React from 'react';
import { format, isValid } from 'date-fns';
import { Memory } from '@/types/memory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { CalendarIcon, Quote } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface MemoryTimelineProps {
  memories: Memory[];
  onSelectMemory: (date: Date) => void;
}

const MemoryTimeline = ({ memories, onSelectMemory }: MemoryTimelineProps) => {
  const isMobile = useIsMobile();

  const formatSafeDate = (dateString: string) => {
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'MMM d, yyyy') : 'Invalid date';
  };

  if (isMobile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Memory Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="max-h-[600px]">
            <div className="space-y-3">
              {memories.map((memory) => (
                <Card 
                  key={memory.id} 
                  className="cursor-pointer hover:bg-muted/10"
                  onClick={() => {
                    const date = new Date(memory.date);
                    if (isValid(date)) {
                      onSelectMemory(date);
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        {memory.type === 'quote' ? (
                          <Quote className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <CalendarIcon className="h-3.5 w-3.5 text-primary" />
                        )}
                        <Badge variant={memory.type === 'quote' ? 'default' : 'secondary'} className="text-[10px] px-2 py-0">
                          {memory.type === 'quote' ? 'Quote' : 'Milestone'}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatSafeDate(memory.date)}
                      </span>
                    </div>
                    
                    <div className="font-medium text-sm mb-1 text-left">
                      <Link to={`/children/${memory.childId}`} className="hover:text-primary">
                        {memory.childName}
                      </Link>
                    </div>
                    
                    <p className="text-sm text-muted-foreground text-left">
                      <Link 
                        to={memory.type === 'quote' ? `/quotes#${memory.id}` : `/milestones#${memory.id}`}
                        className="hover:text-primary"
                      >
                        {memory.content}
                      </Link>
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Memory Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-left">Date</TableHead>
              <TableHead className="text-left">Child</TableHead>
              <TableHead className="text-left">Memory</TableHead>
              <TableHead className="text-center">Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {memories.map((memory) => (
              <TableRow 
                key={memory.id} 
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-muted/10" 
                onClick={() => {
                  const date = new Date(memory.date);
                  if (isValid(date)) {
                    onSelectMemory(date);
                  }
                }}
              >
                <TableCell className="text-left">{formatSafeDate(memory.date)}</TableCell>
                <TableCell className="text-left">
                  <Link to={`/children/${memory.childId}`} className="hover:text-primary">
                    {memory.childName}
                  </Link>
                </TableCell>
                <TableCell className="text-left max-w-xs truncate">
                  <Link 
                    to={memory.type === 'quote' ? `/quotes#${memory.id}` : `/milestones#${memory.id}`}
                    className="hover:text-primary"
                  >
                    {memory.content}
                  </Link>
                </TableCell>
                <TableCell className="text-center">
                  <Link 
                    to={memory.type === 'quote' ? `/quotes` : `/milestones`}
                    className="no-underline"
                  >
                    <Badge variant={memory.type === 'quote' ? 'default' : 'secondary'}>
                      {memory.type === 'quote' ? 'Quote' : 'Milestone'}
                    </Badge>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default MemoryTimeline;
